import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react';
import { Chip, Container, Fade, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery, useTheme } from '@mui/material';
import { HttpClient } from '../../api/HttpClient';
import { RecentRetroRoomsResponse } from '../../api/Requests';
import { AuthState, UserContext } from '../../contexts/UserContext';
import { RetroCategory, RetroAction } from './analytics/RetroTracking';
import { useTracking } from '../../analytics/useTracking';

/**
 * Lists the signed-in user's recently active retro rooms so they can return to a board
 * from any machine without the original link. Only rendered when signed in (see CreateRoomWrapper).
 */
export default function RecentRetroRooms(): ReactElement | null {
  const { authState, authToken } = useContext(UserContext);
  const { track } = useTracking();
  const [response, setResponse] = useState<RecentRetroRoomsResponse>();
  const isFetchingRef = useRef(false);

  const theme = useTheme();
  const isScreenSizeMobile = useMediaQuery(theme.breakpoints.up('sm'));

  useEffect(() => {
    if (authState !== AuthState.SignedIn || isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    const fetchData = async () => {
      try {
        const httpClient = new HttpClient(authToken);
        const r = (await httpClient.get('/retro-rooms/recent')) as RecentRetroRoomsResponse;
        setResponse(r);
      } catch {
        // Recent retros are a convenience; ignore fetch failures silently.
      } finally {
        isFetchingRef.current = false;
      }
    };

    fetchData();
  }, [authState, authToken]);

  const roomLink = (urlId: string) => (isScreenSizeMobile ? window.location.origin + '/retro/' : '') + urlId;

  if (!response?.recentRetroRooms?.length) {
    return null;
  }

  return (
    <Fade in={true}>
      <Container maxWidth="md">
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Recent Retros</TableCell>
                <TableCell align="right">Participants</TableCell>
                <TableCell align="right">Last Visited</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {response.recentRetroRooms.map((row) => (
                <TableRow key={row.urlId}>
                  <TableCell component="th" scope="row">
                    <Link
                      color="inherit"
                      sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1 }}
                      href={window.location.origin + '/retro/' + row.urlId}
                      variant="body1"
                      onClick={() => track.event(RetroCategory.Room, RetroAction.RecentRoomVisited)}
                    >
                      {roomLink(row.urlId)}
                      {row.state === 'closed' && <Chip label="Closed" size="small" variant="outlined" />}
                    </Link>
                  </TableCell>
                  <TableCell align="right">{row.playerNames}</TableCell>
                  <TableCell align="right">
                    {row.daysSinceLastVisit} {row.daysSinceLastVisit === 1 ? 'day' : 'days'} ago
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>
    </Fade>
  );
}
